import React, { useState, useEffect, useCallback } from 'react';
import { entitiesApi } from '../../utils/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './Admin.css';

function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div className="adm-modal-overlay">
      <div className="adm-modal" style={{ maxWidth: 400 }}>
        <div className="adm-modal-head"><h2>Confirm Delete</h2></div>
        <div className="adm-modal-body adm-confirm-body">{message}</div>
        <div className="adm-modal-foot">
          <button className="adm-btn adm-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="adm-btn adm-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

function EntityModal({ entity, onClose }) {
  const isEdit = !!entity;
  const [form, setForm] = useState({
    code: entity?.code || '',
    name: entity?.name || '',
    arabicName: entity?.arabicName || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      if (isEdit) {
        await entitiesApi.update(entity.id, { name: form.name, arabicName: form.arabicName || null });
      } else {
        await entitiesApi.create({ code: form.code, name: form.name, arabicName: form.arabicName || null });
      }
      onClose(true);
    } catch (e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="adm-modal-overlay">
      <div className="adm-modal">
        <div className="adm-modal-head">
          <h2>{isEdit ? `Edit — ${entity.name}` : 'Create Sub-Entity'}</h2>
          <button className="adm-modal-close" onClick={() => onClose(false)}><i className="fas fa-times"></i></button>
        </div>
        <div className="adm-modal-body">
          {error && <div className="adm-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
          {!isEdit && (
            <div className="adm-field">
              <label className="adm-label">Code *</label>
              <input className="adm-input" value={form.code} onChange={(e) => set('code', e.target.value)}
                placeholder="e.g. BRT" autoFocus />
              <p className="adm-field-hint">Short identifier (2–10 chars). Cannot be changed later.</p>
            </div>
          )}
          <div className="adm-field">
            <label className="adm-label">Name (English) *</label>
            <input className="adm-input" value={form.name} onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. British School" autoFocus={isEdit} />
          </div>
          <div className="adm-field">
            <label className="adm-label">Arabic Name</label>
            <input className="adm-input" value={form.arabicName} onChange={(e) => set('arabicName', e.target.value)}
              placeholder="اختياري" dir="rtl" style={{ textAlign: 'right' }} />
          </div>
        </div>
        <div className="adm-modal-foot">
          <button className="adm-btn adm-btn--ghost" onClick={() => onClose(false)}>Cancel</button>
          <button className="adm-btn adm-btn--primary" onClick={submit} disabled={saving}>
            {saving ? <LoadingSpinner size="small" variant="button" /> : (isEdit ? 'Save Changes' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
}

const EntitiesPage = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('entities.manage');

  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setEntities(await entitiesApi.list()); }
    catch (e) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeModal = (refresh) => { setModal(null); if (refresh) load(); };

  const doDelete = async () => {
    try {
      await entitiesApi.remove(modal.entity.id);
      closeModal(true);
    } catch (e) { setError(e.message || 'Delete failed'); setModal(null); }
  };

  return (
    <div className="adm-page">
      <div className="adm-header">
        <div>
          <h1 className="adm-title"><i className="fas fa-building" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Sub-Entities</h1>
          <p className="adm-subtitle">British School · American School · Newgiza Co.</p>
        </div>
        {canManage && (
          <button className="adm-btn adm-btn--primary" onClick={() => setModal({ type: 'create' })}>
            <i className="fas fa-plus"></i> New Entity
          </button>
        )}
      </div>

      {error && <div className="adm-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

      <div className="adm-card">
        {loading ? (
          <LoadingSpinner variant="default" message="Loading entities…" />
        ) : (
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Arabic Name</th>
                  <th>Status</th>
                  {canManage && <th style={{ textAlign: 'right' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {entities.length === 0 && (
                  <tr><td colSpan={canManage ? 5 : 4} className="adm-empty">No entities found.</td></tr>
                )}
                {entities.map((e) => (
                  <tr key={e.id}>
                    <td><code style={{ fontFamily: 'var(--font-family-mono)', fontSize: '0.85rem', fontWeight: 600 }}>{e.code}</code></td>
                    <td><strong>{e.name}</strong></td>
                    <td style={{ direction: 'rtl', textAlign: 'right', fontFamily: 'serif', fontSize: '0.95rem' }}>{e.arabicName || '—'}</td>
                    <td>
                      <span className={`adm-badge ${e.isActive ? 'adm-badge--active' : 'adm-badge--inactive'}`}>
                        {e.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {canManage && (
                      <td>
                        <div className="adm-actions">
                          <button className="adm-btn adm-btn--ghost adm-btn--sm" title="Edit"
                            onClick={() => setModal({ type: 'edit', entity: e })}>
                            <i className="fas fa-pen"></i>
                          </button>
                          <button className="adm-btn adm-btn--danger adm-btn--sm" title="Delete"
                            onClick={() => setModal({ type: 'delete', entity: e })}>
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal?.type === 'create' && <EntityModal onClose={closeModal} />}
      {modal?.type === 'edit'   && <EntityModal entity={modal.entity} onClose={closeModal} />}
      {modal?.type === 'delete' && (
        <ConfirmModal
          message={<>Delete entity <strong>{modal.entity.name}</strong>? This cannot be undone and may affect accounts linked to this entity.</>}
          onConfirm={doDelete}
          onCancel={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default EntitiesPage;
